import type { ReactNode } from 'react'
import * as Icons from 'lucide-react'
import { Building2, MapPinned, Megaphone, type LucideIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { getModuleHref, getModuleKey } from '@/lib/modules'
import type { AppModule, ModuleKey } from '@/types/models/module'

type Props = {
  currentModule: ModuleKey
  modules: AppModule[]
  trigger: ReactNode
}

const fallbackIcons: Record<ModuleKey, LucideIcon> = {
  core: Building2,
  zap: MapPinned,
  comunicaciones: Megaphone,
}

export default function ModuleSwitcherDialog({ currentModule, modules, trigger }: Props) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="w-fit min-w-0 max-w-[calc(100vw-2rem)] px-8 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-intel">Selecciona un módulo</DialogTitle>
        </DialogHeader>
        <div className="flex w-max max-w-full flex-row flex-nowrap gap-3 overflow-x-auto py-2">
          {modules.map((module) => {
            const key = getModuleKey(module)
            const configuredIcon = module.icon
              ? Icons[module.icon as keyof typeof Icons] as LucideIcon | undefined
              : undefined
            const Icon = configuredIcon || fallbackIcons[key]

            return (
              <a
                key={module.id}
                href={getModuleHref(module)}
                aria-current={key === currentModule ? 'page' : undefined}
                className="group flex min-h-20 w-44 shrink-0 flex-col gap-2 rounded-lg border bg-card p-3 text-card-foreground transition-colors hover:border-primary hover:bg-accent/40 aria-[current=page]:border-primary aria-[current=page]:bg-primary/5"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary transition-transform duration-200 group-hover:scale-110">
                  <Icon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                </span>
                <span>
                  <span className="block font-intel text-sm font-semibold">{module.name}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{module.description}</span>
                </span>
              </a>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
